import { useEffect, useRef, useState } from "react";
import { useAuth, useSignIn } from "@clerk/clerk-react";
import { Link, useNavigate } from "react-router-dom";
import { TriangleLogo } from "@/components/TriangleLogo";
import { LegalLinks } from "@/components/shared/LegalLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authErrorMessage } from "@/lib/authMessages";

type Step = "email" | "factor" | "reset";

export default function Login() {
  const { isSignedIn } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [factorStrategy, setFactorStrategy] = useState<"email_code" | "password">("email_code");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const operationInFlight = useRef(false);

  const beginOperation = () => {
    if (operationInFlight.current) return false;
    operationInFlight.current = true;
    setError(null);
    setLoading(true);
    return true;
  };

  const endOperation = () => {
    operationInFlight.current = false;
    setLoading(false);
  };

  useEffect(() => {
    if (isSignedIn) navigate("/");
  }, [isSignedIn, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !beginOperation()) return;
    let signInCreated = false;
    try {
      const result = await signIn.create({ identifier: email });
      signInCreated = true;

      const emailCodeFactor = result.supportedFirstFactors?.find(
        (f) => f.strategy === "email_code",
      );
      const passwordFactor = result.supportedFirstFactors?.find(
        (f) => f.strategy === "password",
      );

      if (passwordFactor) {
        setError(null);
        setFactorStrategy("password");
        setStep("factor");
      } else if (emailCodeFactor && emailCodeFactor.strategy === "email_code") {
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: emailCodeFactor.emailAddressId,
        });
        setError(null);
        setFactorStrategy("email_code");
        setStep("factor");
      } else {
        setError("Método de login não suportado para esta conta.");
      }
    } catch (err: unknown) {
      setError(signInCreated
        ? "Não foi possível enviar o código de acesso. Tente novamente."
        : authErrorMessage(err, "sign-in"));
    } finally {
      endOperation();
    }
  };

  const switchToEmailCode = async () => {
    if (!isLoaded || !signIn || !beginOperation()) return;
    try {
      const emailCodeFactor = signIn.supportedFirstFactors?.find(
        (f) => f.strategy === "email_code",
      );
      if (!emailCodeFactor || emailCodeFactor.strategy !== "email_code") {
        setError("Login por código não disponível para esta conta.");
        return;
      }
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailCodeFactor.emailAddressId,
      });
      setPassword("");
      setFactorStrategy("email_code");
    } catch {
      setError("Não foi possível enviar o código de acesso. Tente novamente.");
    } finally {
      endOperation();
    }
  };

  const switchToPassword = () => {
    if (operationInFlight.current) return;
    setCode("");
    setError(null);
    setFactorStrategy("password");
  };

  const startPasswordReset = async () => {
    if (!isLoaded || !signIn || !beginOperation()) return;
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
      setCode("");
      setPassword("");
      setNewPassword("");
      setError(null);
      setStep("reset");
    } catch (err: unknown) {
      setError(authErrorMessage(err, "reset"));
    } finally {
      endOperation();
    }
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !beginOperation()) return;
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password: newPassword,
      });

      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        navigate("/");
      } else if (result.status === "needs_new_password") {
        setError("Defina uma nova senha para continuar.");
      } else {
        setError("Verificação adicional necessária.");
      }
    } catch (err: unknown) {
      setError(authErrorMessage(err, "reset-verification"));
    } finally {
      endOperation();
    }
  };

  const handleFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !beginOperation()) return;
    try {
      const result =
        factorStrategy === "email_code"
          ? await signIn.attemptFirstFactor({ strategy: "email_code", code })
          : await signIn.attemptFirstFactor({ strategy: "password", password });

      if (result.status === "complete") {
        try {
          await setActive({ session: result.createdSessionId });
          navigate("/");
        } catch {
          setError("Dados confirmados, mas não foi possível iniciar sua sessão. Tente entrar novamente.");
        }
      } else {
        setError("Verificação adicional necessária.");
      }
    } catch (err: unknown) {
      setError(authErrorMessage(err, "sign-in-factor"));
    } finally {
      endOperation();
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      {/* Subtle top glow */}
      <div
        className="pointer-events-none fixed inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, hsl(var(--primary)) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center gap-3 mb-8">
          <TriangleLogo size="lg" animate />
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              Código do Poder
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Instituto Rafael Medeiros</p>
          </div>
        </div>

        {/* Card */}
        <div className="bg-card border border-border/60 rounded-2xl p-8 shadow-xl shadow-black/20">
          <h2 className="text-lg font-semibold text-foreground mb-1">
            {step === "email"
              ? "Entrar na sua conta"
              : step === "reset"
              ? "Redefinir senha"
              : "Verificar identidade"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {step === "email"
              ? "Acesse sua conta para continuar."
              : step === "reset"
              ? `Enviamos um código para ${email}. Digite-o e escolha uma nova senha.`
              : factorStrategy === "email_code"
              ? `Enviamos um código para ${email}.`
              : `Insira a senha de ${email}.`}
          </p>

          {step === "email" && (
            <>
              <form onSubmit={(e) => void handleEmailSubmit(e)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  {/* A6-A11y — wire aria-invalid + aria-describedby to error
                      so SR users associate the failure with this field
                      (WCAG 1.3.1 + 3.3.1). */}
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "login-error" : undefined}
                    className="h-11"
                  />
                </div>

                {error && <ErrorBanner id="login-error" message={error} />}

                <Button
                  type="submit"
                  className="w-full h-11 glow-primary-sm"
                  disabled={!isLoaded || loading || !email}
                >
                  {loading ? "Verificando..." : "Continuar"}
                </Button>
              </form>
            </>
          )}

          {step === "factor" && (
            <form onSubmit={(e) => void handleFactorSubmit(e)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="factor-input">
                  {factorStrategy === "email_code" ? "Código de verificação" : "Senha"}
                </Label>
                {factorStrategy === "email_code" ? (
                  <Input
                    id="factor-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    disabled={loading}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "factor-error" : undefined}
                    className="h-11 tracking-widest text-center text-lg"
                    maxLength={6}
                  />
                ) : (
                  <Input
                    id="factor-input"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "factor-error" : undefined}
                    className="h-11"
                  />
                )}
              </div>

              {error && <ErrorBanner id="factor-error" message={error} />}

              <Button
                type="submit"
                className="w-full h-11 glow-primary-sm"
                disabled={
                  !isLoaded ||
                  loading ||
                  (factorStrategy === "email_code" ? code.length < 6 : !password)
                }
              >
                {loading ? "Verificando..." : "Entrar"}
              </Button>

              {factorStrategy === "password" && (
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => void startPasswordReset()}
                    disabled={loading}
                  >
                    Esqueci minha senha
                  </Button>
                  {signIn?.supportedFirstFactors?.some(
                    (f) => f.strategy === "email_code",
                  ) && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-sm text-muted-foreground hover:text-foreground"
                      onClick={() => void switchToEmailCode()}
                      disabled={loading}
                    >
                      Entrar com código por email
                    </Button>
                  )}
                </div>
              )}

              {factorStrategy === "email_code" &&
                signIn?.supportedFirstFactors?.some(
                  (f) => f.strategy === "password",
                ) && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-sm text-muted-foreground hover:text-foreground"
                    onClick={switchToPassword}
                    disabled={loading}
                  >
                    Entrar com senha
                  </Button>
                )}

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setPassword("");
                  setError(null);
                }}
                disabled={loading}
              >
                ← Voltar
              </Button>
            </form>
          )}

          {step === "reset" && (
            <form onSubmit={(e) => void handleResetSubmit(e)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="reset-code">Código de verificação</Label>
                <Input
                  id="reset-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "reset-error" : undefined}
                  className="h-11 tracking-widest text-center text-lg"
                  maxLength={6}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reset-new-password">Nova senha</Label>
                <Input
                  id="reset-new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={loading}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "reset-error" : undefined}
                  className="h-11"
                />
              </div>

              {error && <ErrorBanner id="reset-error" message={error} />}

              <Button
                type="submit"
                className="w-full h-11 glow-primary-sm"
                disabled={
                  !isLoaded || loading || code.length < 6 || newPassword.length < 8
                }
              >
                {loading ? "Redefinindo..." : "Redefinir e entrar"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setNewPassword("");
                  setError(null);
                }}
                disabled={loading}
              >
                ← Voltar
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Não tem conta?{" "}
          <Link to="/register" className="text-primary hover:underline">
            Cadastre-se
          </Link>
        </p>
        <LegalLinks className="mt-4" openInNewTab />
      </div>
    </main>
  );
}

function ErrorBanner({ id, message }: { id?: string; message: string }) {
  // A5-A11y — role="alert" + aria-live="assertive" so screen readers
  // interrupt-announce the error when the form fails (WCAG 4.1.3).
  // A6-A11y — id allows the failing input to set aria-describedby, linking
  // field ↔ error message for AT users (WCAG 1.3.1 + 3.3.1).
  return (
    <p
      id={id}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className="text-sm text-destructive bg-destructive/15 border border-destructive/30 rounded-lg px-3 py-2"
    >
      {message}
    </p>
  );
}
